import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { freeShipping, FreeShippingConfig, FreeShippingLanguage } from '@/lib/api'
import {
  Save, Package, Clock, MessageSquare, TrendingUp, AlertCircle, Globe,
  Gift, CheckCircle2, Calendar, Euro, Settings, Languages, History,
  Info
} from 'lucide-react'
import { useToast } from '@/components/ui/Toast'

const DEFAULT_MESSAGE = 'Envio gratuito disponible - Tienes un pedido anterior (#{order_reference}) pendiente de envio a esta misma direccion'

// Mensajes por defecto por idioma
const DEFAULT_MESSAGES: Record<string, string> = {
  es: 'Envio gratuito disponible - Tienes un pedido anterior (#{order_reference}) pendiente de envio a esta misma direccion',
  pt: 'Envio gratuito disponivel - Voce tem um pedido anterior (#{order_reference}) pendente de envio para este mesmo endereco',
  en: 'Free shipping available - You have a previous order (#{order_reference}) pending shipment to this same address',
  fr: 'Livraison gratuite disponible - Vous avez une commande precedente (#{order_reference}) en attente d\'expedition a cette meme adresse',
  de: 'Kostenloser Versand verfugbar - Sie haben eine fruhere Bestellung (#{order_reference}) mit Versand an dieselbe Adresse ausstehend',
}

export default function FreeShippingPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<Partial<FreeShippingConfig>>({
    enabled: false,
    id_carrier_free: 0,
    time_window_start: '16:30',
    time_window_end: '16:00',
    valid_order_states: [2, 4],
    show_message: true,
    message_template: DEFAULT_MESSAGE,
  })
  const [hasChanges, setHasChanges] = useState(false)
  const [selectedLang, setSelectedLang] = useState<number>(1) // Default to ES
  const [translations, setTranslations] = useState<Record<number, string>>({})
  const [translationsChanged, setTranslationsChanged] = useState(false)

  // Queries
  const configQuery = useQuery({
    queryKey: ['free-shipping-config'],
    queryFn: freeShipping.getConfig,
  })

  const carriersQuery = useQuery({
    queryKey: ['free-shipping-carriers'],
    queryFn: freeShipping.getCarriers,
  })

  const statesQuery = useQuery({
    queryKey: ['free-shipping-states'],
    queryFn: freeShipping.getOrderStates,
  })

  const statsQuery = useQuery({
    queryKey: ['free-shipping-stats'],
    queryFn: freeShipping.getStats,
  })

  const languagesQuery = useQuery({
    queryKey: ['free-shipping-languages'],
    queryFn: freeShipping.getLanguages,
  })

  const translationsQuery = useQuery({
    queryKey: ['free-shipping-translations', configQuery.data?.id_config],
    queryFn: () => configQuery.data?.id_config ? freeShipping.getTranslations(configQuery.data.id_config) : Promise.resolve({}),
    enabled: !!configQuery.data?.id_config,
  })

  // Mutation
  const updateMutation = useMutation({
    mutationFn: freeShipping.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['free-shipping-config'] })
      queryClient.invalidateQueries({ queryKey: ['free-shipping-stats'] })
      setHasChanges(false)
      toast.success('Configuracion guardada correctamente')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const translationsMutation = useMutation({
    mutationFn: ({ idConfig, translations }: { idConfig: number; translations: Record<number, string> }) =>
      freeShipping.updateTranslations(idConfig, translations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['free-shipping-translations'] })
      setTranslationsChanged(false)
      toast.success('Traducciones guardadas correctamente')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  // Load config into form
  useEffect(() => {
    if (configQuery.data) {
      setFormData({
        enabled: configQuery.data.enabled,
        id_carrier_free: configQuery.data.id_carrier_free,
        time_window_start: configQuery.data.time_window_start?.substring(0, 5) || '16:30',
        time_window_end: configQuery.data.time_window_end?.substring(0, 5) || '16:00',
        valid_order_states: configQuery.data.valid_order_states || [2, 4],
        show_message: configQuery.data.show_message,
        message_template: configQuery.data.message_template || DEFAULT_MESSAGE,
      })
    }
  }, [configQuery.data])

  // Load translations
  useEffect(() => {
    if (translationsQuery.data) {
      setTranslations(translationsQuery.data)
    }
  }, [translationsQuery.data])

  const handleTranslationChange = (langId: number, value: string) => {
    setTranslations(prev => ({ ...prev, [langId]: value }))
    setTranslationsChanged(true)
  }

  const handleSaveTranslations = () => {
    if (configQuery.data?.id_config) {
      translationsMutation.mutate({
        idConfig: configQuery.data.id_config,
        translations,
      })
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.id_carrier_free) {
      toast.warning('Debe seleccionar un transportista gratuito')
      return
    }
    updateMutation.mutate({
      enabled: formData.enabled || false,
      id_carrier_free: formData.id_carrier_free || 0,
      time_window_start: formData.time_window_start || '16:30',
      time_window_end: formData.time_window_end || '16:00',
      valid_order_states: formData.valid_order_states || [2, 4],
      show_message: formData.show_message || true,
      message_template: formData.message_template || DEFAULT_MESSAGE,
    })
  }

  const handleChange = (field: keyof FreeShippingConfig, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleStateToggle = (stateId: number) => {
    const current = formData.valid_order_states || []
    const newStates = current.includes(stateId)
      ? current.filter(s => s !== stateId)
      : [...current, stateId]
    handleChange('valid_order_states', newStates)
  }

  const isLoading = configQuery.isLoading || carriersQuery.isLoading || statesQuery.isLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
          <div className="w-5 h-5 border-2 border-slate-300 border-t-primary-500 rounded-full animate-spin" />
          Cargando configuracion...
        </div>
      </div>
    )
  }

  const selectedCarrier = carriersQuery.data?.find(c => c.id_carrier === formData.id_carrier_free)

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg shadow-green-500/20">
          <Gift className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Envio Gratuito Agrupado
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Ofrece envio gratuito a clientes con pedidos anteriores pendientes de envio a la misma direccion
          </p>
        </div>

        {/* Status Badge */}
        <div className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${
          formData.enabled
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
        }`}>
          <span className={`w-2 h-2 rounded-full ${formData.enabled ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
          {formData.enabled ? 'Activo' : 'Inactivo'}
        </div>
      </div>

      {/* Stats Cards */}
      {statsQuery.data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Today */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Hoy</p>
                <p className="text-3xl font-bold text-green-700 dark:text-green-300 mt-1">
                  {statsQuery.data.total_applied_today}
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-xl">
                <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-xs text-green-600/70 dark:text-green-400/70 mt-2">
              Envios gratuitos aplicados
            </p>
          </div>

          {/* This Week */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Esta Semana</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                  {statsQuery.data.total_applied_week}
                </p>
              </div>
              <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                <Calendar className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Ultimos 7 dias
            </p>
          </div>

          {/* This Month */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Este Mes</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                  {statsQuery.data.total_applied_month}
                </p>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                <Package className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Acumulado mensual
            </p>
          </div>

          {/* Estimated Savings */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Ahorro Estimado</p>
                <p className="text-3xl font-bold text-amber-700 dark:text-amber-300 mt-1">
                  {statsQuery.data.estimated_savings.toFixed(0)}€
                </p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-xl">
                <Euro className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-2">
              En costes de envio este mes
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Main Configuration */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <Settings className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Configuracion General
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Activa el servicio y selecciona el transportista gratuito
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl transition-colors ${
                  formData.enabled
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-slate-200 dark:bg-slate-700'
                }`}>
                  {formData.enabled ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <Gift className="w-6 h-6 text-slate-500 dark:text-slate-400" />
                  )}
                </div>
                <div>
                  <div className="font-medium text-slate-900 dark:text-white">
                    Estado del Servicio
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {formData.enabled
                      ? 'Los clientes elegibles veran la opcion de envio gratuito'
                      : 'El servicio esta desactivado para todos los clientes'
                    }
                  </div>
                </div>
              </div>

              {/* Modern Toggle Switch */}
              <button
                type="button"
                onClick={() => handleChange('enabled', !formData.enabled)}
                className={`relative w-14 h-8 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
                  formData.enabled
                    ? 'bg-green-500'
                    : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                  formData.enabled ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {/* Carrier Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Transportista Gratuito
              </label>
              <select
                value={formData.id_carrier_free || ''}
                onChange={(e) => handleChange('id_carrier_free', parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">Seleccionar transportista...</option>
                {carriersQuery.data?.map((carrier) => (
                  <option key={carrier.id_carrier} value={carrier.id_carrier}>
                    {carrier.name} {carrier.is_free ? '(Gratuito)' : ''} {!carrier.active ? '(Inactivo)' : ''}
                  </option>
                ))}
              </select>
              {selectedCarrier && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Este transportista aparecera como opcion cuando el cliente sea elegible
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Time Window */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Franja Horaria
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Ventana de tiempo para considerar pedidos anteriores (solo dias laborables)
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Hora de Inicio
                </label>
                <input
                  type="time"
                  value={formData.time_window_start || '16:30'}
                  onChange={(e) => handleChange('time_window_start', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Inicio de la franja (dia N)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Hora de Fin
                </label>
                <input
                  type="time"
                  value={formData.time_window_end || '16:00'}
                  onChange={(e) => handleChange('time_window_end', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Fin de la franja (siguiente dia laborable)
                </p>
              </div>
            </div>

            {/* Visual explanation */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <strong className="font-semibold">Ejemplo:</strong> Con franja 16:30 - 16:00, un pedido del viernes a las 17:00
                  permitira envio gratuito hasta el lunes a las 16:00. Los fines de semana no cuentan como dias laborables.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order States */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Estados de Pedido Validos
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Solo pedidos en estos estados activan el beneficio
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {statesQuery.data?.map((state) => {
                const isSelected = formData.valid_order_states?.includes(state.id_order_state)
                return (
                  <button
                    key={state.id_order_state}
                    type="button"
                    onClick={() => handleStateToggle(state.id_order_state)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-slate-300 dark:border-slate-600'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white dark:ring-slate-800"
                      style={{ backgroundColor: state.color }}
                    />
                    <span className={`text-sm font-medium ${
                      isSelected
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-slate-700 dark:text-slate-300'
                    }`}>
                      {state.name}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Customer Message */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <MessageSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Mensaje al Cliente
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Notificacion que se muestra en el checkout
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
              <div className="flex items-center gap-3">
                <MessageSquare className={`w-5 h-5 ${formData.show_message ? 'text-emerald-500' : 'text-slate-400'}`} />
                <div>
                  <div className="font-medium text-slate-900 dark:text-white">
                    Mostrar mensaje informativo
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    El cliente vera una notificacion en el checkout
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleChange('show_message', !formData.show_message)}
                className={`relative w-14 h-8 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
                  formData.show_message
                    ? 'bg-emerald-500'
                    : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                  formData.show_message ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {formData.show_message && (
              <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                  <Languages className="w-4 h-4" />
                  <span className="text-sm font-medium">Configura los mensajes por idioma en la seccion de abajo</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Multilingual Messages */}
        {formData.show_message && languagesQuery.data && languagesQuery.data.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <Globe className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Mensajes por Idioma
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Personaliza el mensaje para cada idioma de la tienda
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Language Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {languagesQuery.data.map((lang: FreeShippingLanguage) => (
                  <button
                    key={lang.id_lang}
                    type="button"
                    onClick={() => setSelectedLang(lang.id_lang)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      selectedLang === lang.id_lang
                        ? 'bg-indigo-500 text-white shadow-md'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {lang.iso_code.toUpperCase()} - {lang.name}
                  </button>
                ))}
              </div>

              {/* Message Editor for Selected Language */}
              {languagesQuery.data.map((lang: FreeShippingLanguage) => (
                <div
                  key={lang.id_lang}
                  className={selectedLang === lang.id_lang ? 'block' : 'hidden'}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Mensaje en {lang.name}
                    </label>
                    {!translations[lang.id_lang] && (
                      <span className="text-xs text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-2 py-1 rounded-full">
                        Usando mensaje por defecto
                      </span>
                    )}
                  </div>
                  <textarea
                    value={translations[lang.id_lang] || ''}
                    onChange={(e) => handleTranslationChange(lang.id_lang, e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                    placeholder={DEFAULT_MESSAGES[lang.iso_code] || DEFAULT_MESSAGE}
                  />
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    <span>Variables disponibles:</span>
                    <code className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{'{order_reference}'}</code>
                    <code className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{'{order_date}'}</code>
                    <code className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{'{order_total}'}</code>
                  </div>

                  {/* Preview */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div className="text-sm font-medium text-green-800 dark:text-green-200 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Vista previa ({lang.iso_code.toUpperCase()})
                    </div>
                    <div className="text-green-700 dark:text-green-300 text-sm">
                      {(translations[lang.id_lang] || DEFAULT_MESSAGES[lang.iso_code] || DEFAULT_MESSAGE)
                        .replace('{order_reference}', 'ABCD1234')
                        .replace('{order_date}', '02/12/2024')
                        .replace('{order_total}', '125.50 EUR')}
                    </div>
                  </div>
                </div>
              ))}

              {/* Save Translations Button */}
              <div className="flex justify-end mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={handleSaveTranslations}
                  disabled={translationsMutation.isPending || !translationsChanged}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    translationsChanged
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  <Save className="w-4 h-4" />
                  {translationsMutation.isPending ? 'Guardando...' : 'Guardar Traducciones'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end sticky bottom-4">
          <button
            type="submit"
            disabled={updateMutation.isPending || !hasChanges}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 shadow-lg ${
              hasChanges
                ? 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-xl'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed shadow-none'
            }`}
          >
            <Save className="w-5 h-5" />
            {updateMutation.isPending ? 'Guardando...' : 'Guardar Configuracion'}
          </button>
        </div>
      </form>

      {/* Recent Applications */}
      {statsQuery.data?.recent_applications && statsQuery.data.recent_applications.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <History className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Aplicaciones Recientes
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Ultimos envios gratuitos aplicados
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Pedido
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Pedido Relacionado
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {statsQuery.data.recent_applications.map((app, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-white">
                      {app.customer_name}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-primary-600 dark:text-primary-400">
                      {app.order_reference}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-600 dark:text-slate-400">
                      #{app.related_order}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                      {app.date ? new Date(app.date).toLocaleString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-white text-right">
                      {app.total_paid.toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
